import { diffInternals } from "../shared/diff";

const { normalizedEqual, DEVICE_FK_FIELDS, CAMERA_FK_FIELDS, VIEW_FK_FIELDS, RESOLUTION_FIELDS } = diffInternals;

// Maximum number of views a single camera may hold. Frontend guardrail only —
// there is no backend enforcement in scope, so this is a UX cap, not an
// integrity boundary.
export const MAX_VIEWS_PER_CAMERA = 3;

// Number of views that count toward the cap: active (not soft-deleted) ones.
// A view marked for deletion no longer occupies a slot, so deleting one frees
// room to add a replacement before the next save. Single source of truth for
// the cap so the reducer guards and the UI gating can never diverge.
export function activeViewCount(views) {
  return (views || []).filter((v) => !v.localDeleted).length;
}

// A camera must always keep at least one active view: `viewId` is the last
// active (non-deleted) view of `camera` iff no sibling could take over.
// Single source of truth for the reducer guards and the UI gating.
export function isLastActiveView(camera, viewId) {
  const active = (camera?.views || []).filter((v) => !v.localDeleted && v.is_active !== false);
  return active.length === 1 && active[0].public_id === viewId;
}

/**
 * Render a per-camera field template against a slot number.
 *
 * Supported placeholders: `{slot}` (as-is) and `{slot:N}` (zero-padded to N
 * digits, e.g. `{slot:2}` → "01"). Everything else is literal, so patterns
 * like Hikvision's `/Streaming/Channels/{slot}02` need no arithmetic.
 *
 * The pad width is capped at {@link MAX_SLOT_PAD}: the preview renders on every
 * keystroke, and an uncapped `{slot:999999999}` would make padStart allocate a
 * gigabyte-scale string (or throw) mid-typing.
 *
 * @param {string} template - The template text.
 * @param {number} slotNumber - The camera's slot number.
 * @returns {string} The rendered value.
 */
export const MAX_SLOT_PAD = 10;

export function renderSlotTemplate(template, slotNumber) {
  return String(template).replace(/\{slot(?::(\d+))?\}/g, (match, pad) => {
    if (!pad) return String(slotNumber);
    return String(slotNumber).padStart(Math.min(Number(pad), MAX_SLOT_PAD), "0");
  });
}

/** True when the text carries at least one `{slot}` / `{slot:N}` placeholder. */
export function hasSlotPlaceholder(template) {
  return /\{slot(?::\d+)?\}/.test(String(template || ""));
}

let tempCounter = 0;

export function tempPublicId(prefix) {
  tempCounter += 1;
  return `${prefix}_TEMP_${Date.now()}_${tempCounter}`;
}

export function isTemp(publicId) {
  return typeof publicId === "string" && publicId.includes("_TEMP_");
}

function deepClone(value) {
  return JSON.parse(JSON.stringify(value));
}

export function entityKindFromId(publicId) {
  if (publicId.startsWith("cd_")) return "device";
  if (publicId.startsWith("cam_")) return "camera";
  if (publicId.startsWith("view_")) return "view";
  return null;
}

function maxBy(list, key) {
  return (list || []).reduce((m, item) => Math.max(m, item[key] || 0), 0);
}

function newDevice(partial) {
  return {
    public_id: tempPublicId("cd"),
    name: "",
    brand: null,
    transport: "RTSP",
    host: "",
    rtsp_port: 554,
    api_port: null,
    rtsp_port_redirected: null,
    api_port_redirected: null,
    username: "",
    password: "",
    // Fixed default — the field is not exposed in the forms anymore.
    caching: 3,
    set_date: null,
    // A device is never useful empty: seed camera 1 (which itself seeds view 1).
    cameras: [newCamera(1, {})],
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    ...partial,
  };
}

function newCamera(slot, partial) {
  return {
    public_id: tempPublicId("cam"),
    slot_number: slot,
    stream_quality: "MAIN",
    url_mode: "EXPLICIT",
    rtsp_url_suffix: "",
    protocol: "TCP",
    inference_machine: null,
    alert_channel: null,
    deep_model: null,
    depth_of_field: null,
    is_active: true,
    // A camera without a view cannot alert: seed view 1 alongside it.
    views: [newView(1, {})],
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    ...partial,
  };
}

function newView(viewNumber, partial) {
  return {
    public_id: tempPublicId("view"),
    view_number: viewNumber,
    view_name: "",
    view_area: null,
    resolution: null,
    body_sensitivity: null,
    bag_sensitivity: null,
    relevance: null,
    favor_factor: null,
    body_favor_factor: null,
    default_gesture_favor_factor: null,
    recheckings: null,
    is_active: true,
    screenshot_exist: false,
    body_theft: null,
    gesture_into_body: null,
    product_in_personal_bag: null,
    product_into_backpack: null,
    suspicious_bag: null,
    product_into_stroller: null,
    client_normal_bag: null,
    store_normal_bag: null,
    deblistering: null,
    consumption: null,
    burst_shot: null,
    other_suspicious: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    ...partial,
  };
}

function cloneCamera(camera, slot) {
  return {
    ...camera,
    public_id: tempPublicId("cam"),
    slot_number: slot,
    localDeleted: false,
    // Copy the effective state: drop views the user marked for deletion (cloning
    // must not revive them), THEN trim to the cap — a legacy/server camera may
    // carry more than MAX_VIEWS_PER_CAMERA views and the clone must not exceed it.
    views: (camera.views || [])
      .filter((v) => !v.localDeleted)
      .slice(0, MAX_VIEWS_PER_CAMERA)
      .map((v) => ({
        ...v,
        public_id: tempPublicId("view"),
        localDeleted: false,
        // The clone is another channel, whose stream size nobody has checked: the
        // source frame would reach inference as this view's resolution. Its polygons
        // are kept as copied, pinned in the source's coordinates: that is what a
        // clone stored before frames were persisted, anchor detection reads them
        // without a frame, and the operator sets the real frame on the new camera.
        resolution: null,
      })),
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };
}

function stripLocalDeleted(entity) {
  const { localDeleted, ...rest } = entity;
  return rest;
}

// Mark a server entity for deletion (visually cascades down: server children get
// localDeleted=true so the UI bars them; TEMP children are dropped since they
// were never going to be sent anyway).
function markDeviceDeleted(device) {
  return {
    ...device,
    localDeleted: true,
    cameras: (device.cameras || [])
      .filter((c) => !isTemp(c.public_id))
      .map((c) => ({
        ...c,
        localDeleted: true,
        views: (c.views || []).filter((v) => !isTemp(v.public_id)).map((v) => ({ ...v, localDeleted: true })),
      })),
  };
}

function markCameraDeleted(camera) {
  return {
    ...camera,
    localDeleted: true,
    views: (camera.views || []).filter((v) => !isTemp(v.public_id)).map((v) => ({ ...v, localDeleted: true })),
  };
}

function findServerNode(serverTree, publicId, kind) {
  if (kind === "device") return (serverTree || []).find((d) => d.public_id === publicId) || null;
  if (kind === "camera") {
    for (let i = 0; i < (serverTree || []).length; i += 1) {
      const found = (serverTree[i].cameras || []).find((c) => c.public_id === publicId);
      if (found) return found;
    }
    return null;
  }
  if (kind === "view") {
    for (let i = 0; i < (serverTree || []).length; i += 1) {
      const cameras = serverTree[i].cameras || [];
      for (let j = 0; j < cameras.length; j += 1) {
        const found = (cameras[j].views || []).find((v) => v.public_id === publicId);
        if (found) return found;
      }
    }
    return null;
  }
  return null;
}

function findCameraInTree(tree, cameraId) {
  for (let i = 0; i < (tree || []).length; i += 1) {
    const found = (tree[i].cameras || []).find((c) => c.public_id === cameraId);
    if (found) return found;
  }
  return null;
}

function findCameraOfView(tree, viewId) {
  for (let i = 0; i < (tree || []).length; i += 1) {
    const cameras = tree[i].cameras || [];
    for (let j = 0; j < cameras.length; j += 1) {
      if ((cameras[j].views || []).some((v) => v.public_id === viewId)) return cameras[j];
    }
  }
  return null;
}

export const initialState = {
  serverTree: [],
  tree: [],
  loading: true,
  error: null,
  saving: false,
  lastError: null,
};

export function treeReducer(state, action) {
  switch (action.type) {
    case "LOAD_START":
      return { ...state, loading: true, error: null };

    case "LOAD_OK": {
      const fresh = action.tree || [];
      return {
        ...state,
        tree: deepClone(fresh),
        serverTree: deepClone(fresh),
        loading: false,
        error: null,
        saving: false,
        lastError: null,
      };
    }

    case "LOAD_ERROR":
      return { ...state, loading: false, error: action.error };

    case "ADD_DEVICE":
      return { ...state, tree: [...state.tree, newDevice(action.partial)] };

    case "UPDATE_DEVICE": {
      const tree = state.tree.map((d) => {
        if (d.public_id !== action.publicId) return d;
        return { ...d, ...action.patch };
      });
      // Clear any pending field-level error on the same entity+field.
      const lastError = clearMatchingError(state.lastError, action.publicId, action.patch);
      return { ...state, tree, lastError };
    }

    case "DELETE_DEVICE": {
      const device = state.tree.find((d) => d.public_id === action.publicId);
      if (!device) return state;
      if (isTemp(action.publicId)) {
        return { ...state, tree: state.tree.filter((d) => d.public_id !== action.publicId) };
      }
      return {
        ...state,
        tree: state.tree.map((d) => (d.public_id === action.publicId ? markDeviceDeleted(d) : d)),
      };
    }

    case "DUPLICATE_DEVICE": {
      const original = state.tree.find((d) => d.public_id === action.publicId);
      if (!original) return state;
      const dup = {
        ...original,
        public_id: tempPublicId("cd"),
        localDeleted: false,
        name: `${original.name || "device"} (copy)`,
        cameras: (original.cameras || []).map((c, idx) => cloneCamera(c, idx + 1)),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      return { ...state, tree: [...state.tree, dup] };
    }

    case "ADD_CAMERA": {
      const tree = state.tree.map((d) => {
        if (d.public_id !== action.deviceId) return d;
        const slot = maxBy(d.cameras, "slot_number") + 1;
        return { ...d, cameras: [...(d.cameras || []), newCamera(slot, action.partial)] };
      });
      return { ...state, tree };
    }

    case "UPDATE_CAMERA": {
      const tree = state.tree.map((d) => ({
        ...d,
        cameras: (d.cameras || []).map((c) => {
          if (c.public_id !== action.publicId) return c;
          return { ...c, ...action.patch };
        }),
      }));
      const lastError = clearMatchingError(state.lastError, action.publicId, action.patch);
      return { ...state, tree, lastError };
    }

    case "DELETE_CAMERA": {
      const tree = state.tree.map((d) => ({
        ...d,
        cameras: (d.cameras || [])
          .map((c) => {
            if (c.public_id !== action.publicId) return c;
            if (isTemp(action.publicId)) return null;
            return markCameraDeleted(c);
          })
          .filter(Boolean),
      }));
      return { ...state, tree };
    }

    case "DUPLICATE_CAMERA": {
      const tree = state.tree.map((d) => {
        const original = (d.cameras || []).find((c) => c.public_id === action.publicId);
        if (!original) return d;
        const slot = maxBy(d.cameras, "slot_number") + 1;
        return { ...d, cameras: [...d.cameras, cloneCamera(original, slot)] };
      });
      return { ...state, tree };
    }

    case "ADD_VIEW": {
      const target = findCameraInTree(state.tree, action.cameraId);
      // Cap reached: no-op (buttons are disabled in the UI, this is the guard).
      if (target && activeViewCount(target.views) >= MAX_VIEWS_PER_CAMERA) return state;
      const tree = state.tree.map((d) => ({
        ...d,
        cameras: (d.cameras || []).map((c) => {
          if (c.public_id !== action.cameraId) return c;
          const num = maxBy(c.views, "view_number") + 1;
          return { ...c, views: [...(c.views || []), newView(num, action.partial)] };
        }),
      }));
      return { ...state, tree };
    }

    case "UPDATE_VIEW": {
      let { patch } = action;
      // Deactivation guard: the last active view of a camera cannot be turned
      // off (the checkbox is disabled in the UI, this is the reducer guard).
      if (patch && patch.is_active === false) {
        const owner = findCameraOfView(state.tree, action.publicId);
        if (owner && isLastActiveView(owner, action.publicId)) {
          const { is_active: dropped, ...rest } = patch;
          if (Object.keys(rest).length === 0) return state;
          patch = rest;
        }
      }
      const tree = state.tree.map((d) => ({
        ...d,
        cameras: (d.cameras || []).map((c) => ({
          ...c,
          views: (c.views || []).map((v) => {
            if (v.public_id !== action.publicId) return v;
            return { ...v, ...patch };
          }),
        })),
      }));
      const lastError = clearMatchingError(state.lastError, action.publicId, patch);
      return { ...state, tree, lastError };
    }

    case "DELETE_VIEW": {
      // Keep-one-active guard: deleting the camera's last active view is
      // barred (buttons are disabled in the UI, this is the reducer guard).
      const owner = findCameraOfView(state.tree, action.publicId);
      if (owner && isLastActiveView(owner, action.publicId)) return state;
      const tree = state.tree.map((d) => ({
        ...d,
        cameras: (d.cameras || []).map((c) => ({
          ...c,
          views: (c.views || [])
            .map((v) => {
              if (v.public_id !== action.publicId) return v;
              if (isTemp(action.publicId)) return null;
              return { ...v, localDeleted: true };
            })
            .filter(Boolean),
        })),
      }));
      return { ...state, tree };
    }

    case "DUPLICATE_VIEW": {
      const owner = findCameraOfView(state.tree, action.publicId);
      // Cap reached on the owning camera: no-op.
      if (owner && activeViewCount(owner.views) >= MAX_VIEWS_PER_CAMERA) return state;
      const tree = state.tree.map((d) => ({
        ...d,
        cameras: (d.cameras || []).map((c) => {
          const original = (c.views || []).find((v) => v.public_id === action.publicId);
          if (!original) return c;
          const num = maxBy(c.views, "view_number") + 1;
          const dup = {
            ...original,
            public_id: tempPublicId("view"),
            view_number: num,
            localDeleted: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          };
          return { ...c, views: [...c.views, dup] };
        }),
      }));
      return { ...state, tree };
    }

    // Apply one patch to several same-kind entities at once. A single action
    // rather than N UPDATE_* dispatches, so the change reads as one edit and
    // REVERT_ENTITY still undoes it entity by entity.
    case "BULK_UPDATE": {
      const ids = new Set(action.publicIds || []);
      const patch = action.patch || {};
      if (ids.size === 0 || Object.keys(patch).length === 0) return state;

      // Keep-one-active guard for bulk view deactivation: a camera must always
      // keep at least one active view. When every active view of a camera is in
      // the selection, the one with the lowest view_number keeps is_active.
      let keepActive = null;
      if (action.entityType === "view" && patch.is_active === false) {
        keepActive = new Set();
        state.tree.forEach((d) =>
          (d.cameras || []).forEach((c) => {
            const active = (c.views || []).filter((v) => !v.localDeleted && v.is_active !== false);
            if (active.length > 0 && active.every((v) => ids.has(v.public_id))) {
              const keep = active.reduce((m, v) => ((v.view_number || 0) < (m.view_number || 0) ? v : m));
              keepActive.add(keep.public_id);
            }
          })
        );
      }

      // Entities marked for deletion are never patched: they are on their way
      // out and the changeset would emit a pointless update before the delete.
      const apply = (entity) => {
        if (!ids.has(entity.public_id) || entity.localDeleted) return entity;
        if (keepActive && keepActive.has(entity.public_id)) {
          const { is_active: dropped, ...rest } = patch;
          return Object.keys(rest).length > 0 ? { ...entity, ...rest } : entity;
        }
        return { ...entity, ...patch };
      };

      const tree = mapEntitiesOfKind(state.tree, action.entityType, apply);
      if (!tree) return state;

      return { ...state, tree, lastError: clearBulkError(state.lastError, ids, patch) };
    }

    // Apply a `{slot}` template to one camera field across a selection: every
    // camera gets the template rendered against its OWN slot_number, which a
    // shared BULK_UPDATE patch cannot express.
    case "BULK_APPLY_TEMPLATE": {
      const ids = new Set(action.publicIds || []);
      const { field, template } = action;
      if (!field || !template || ids.size === 0) return state;

      const apply = (camera) => {
        if (!ids.has(camera.public_id) || camera.localDeleted) return camera;
        return { ...camera, [field]: renderSlotTemplate(template, camera.slot_number ?? 0) };
      };

      const tree = mapEntitiesOfKind(state.tree, "camera", apply);
      return { ...state, tree, lastError: clearBulkError(state.lastError, ids, { [field]: null }) };
    }

    // Undo one field across a selection: every entity goes back to its OWN server
    // value, which a patch cannot express — hence a reducer action rather than a
    // BULK_UPDATE from the form. Field-level counterpart of REVERT_ENTITY.
    case "BULK_REVERT_FIELD": {
      const ids = new Set(action.publicIds || []);
      const { field, entityType } = action;
      if (!field || ids.size === 0) return state;

      const revert = (entity) => {
        if (!ids.has(entity.public_id)) return entity;
        const serverNode = findServerNode(state.serverTree, entity.public_id, entityType);
        // No server counterpart (TEMP entity): nothing to go back to.
        if (!serverNode) return entity;
        const serverValue = serverNode[field];
        return {
          ...entity,
          [field]: typeof serverValue === "object" && serverValue !== null ? deepClone(serverValue) : serverValue,
        };
      };

      const tree = mapEntitiesOfKind(state.tree, entityType, revert);
      if (!tree) return state;

      return { ...state, tree, lastError: clearBulkError(state.lastError, ids, { [field]: null }) };
    }

    case "REVERT_ENTITY": {
      const { publicId } = action;
      const kind = entityKindFromId(publicId);
      if (!kind) return state;
      const serverNode = findServerNode(state.serverTree, publicId, kind);

      if (kind === "device") {
        if (!serverNode) {
          // TEMP device → drop it.
          return { ...state, tree: state.tree.filter((d) => d.public_id !== publicId) };
        }
        return {
          ...state,
          tree: state.tree.map((d) => (d.public_id === publicId ? deepClone(serverNode) : d)),
        };
      }

      if (kind === "camera") {
        const tree = state.tree.map((d) => ({
          ...d,
          cameras: serverNode
            ? (d.cameras || []).map((c) => (c.public_id === publicId ? deepClone(serverNode) : c))
            : (d.cameras || []).filter((c) => c.public_id !== publicId),
        }));
        return { ...state, tree };
      }

      // view
      const tree = state.tree.map((d) => ({
        ...d,
        cameras: (d.cameras || []).map((c) => ({
          ...c,
          views: serverNode
            ? (c.views || []).map((v) => (v.public_id === publicId ? deepClone(serverNode) : v))
            : (c.views || []).filter((v) => v.public_id !== publicId),
        })),
      }));
      return { ...state, tree };
    }

    case "SAVE_START":
      return { ...state, saving: true, lastError: null };

    case "SAVE_OK": {
      const { tempToReal = {}, versionUpdates = {}, deleted = new Set(), sentTree } = action;

      // Transform a node: rename TEMP→real ids, refresh updated_at to the version the
      // server returned, drop the localDeleted marker. Field values are preserved.
      const transform = (entity) => {
        const stripped = stripLocalDeleted(entity);
        const realId = tempToReal[stripped.public_id] || stripped.public_id;
        return {
          ...stripped,
          public_id: realId,
          updated_at: versionUpdates[realId] || stripped.updated_at,
        };
      };

      const reconcile = (tree) =>
        tree
          .filter((d) => !deleted.has(d.public_id))
          .map((d) => {
            const td = transform(d);
            return {
              ...td,
              cameras: (d.cameras || [])
                .filter((c) => !deleted.has(c.public_id))
                .map((c) => {
                  const tc = transform(c);
                  return {
                    ...tc,
                    views: (c.views || []).filter((v) => !deleted.has(v.public_id)).map(transform),
                  };
                }),
            };
          });

      // Working tree: preserve user's in-flight edits (whatever's in state.tree right now),
      // just rename TEMP ids of entities the server confirmed created.
      const newTree = reconcile(state.tree);

      // Server snapshot: derive from sentTree (what we actually shipped) so in-flight
      // keystrokes that landed between SAVE_START and SAVE_OK stay flagged as "modified"
      // and ride in the next changeset, instead of being silently absorbed as pristine.
      // Fallback to newTree if sentTree wasn't passed (safety net).
      const newServerTree = Array.isArray(sentTree) ? reconcile(sentTree) : newTree;

      return {
        ...state,
        tree: newTree,
        serverTree: deepClone(newServerTree),
        saving: false,
        lastError: null,
      };
    }

    case "SAVE_ERROR":
      return { ...state, saving: false, lastError: action.error || null };

    case "CLEAR_LAST_ERROR":
      return { ...state, lastError: null };

    case "STALE_REFRESH": {
      const { publicId, freshNode } = action;
      const kind = entityKindFromId(publicId);
      if (!kind || !freshNode) return state;

      const oldServerNode = findServerNode(state.serverTree, publicId, kind);
      // No baseline → we cannot tell which fields the user touched, so adopting
      // freshNode blindly would silently overwrite a coworker's write. Surface a
      // hard error and leave the trees untouched (Discard to reload).
      if (!oldServerNode) {
        return {
          ...state,
          lastError: {
            publicId,
            code: "stale_version_no_baseline",
            message: "Stale version on an entity not in the server snapshot. Discard to reload.",
          },
        };
      }

      // Scalar per-field merge: for fields the user did NOT touch since the last
      // server load (working === oldServer), adopt the fresh server value. For
      // fields they DID touch, keep their working value. Children (cameras/views)
      // are intentionally NOT merged here — see below.
      const fkFields = kind === "device" ? DEVICE_FK_FIELDS : kind === "camera" ? CAMERA_FK_FIELDS : VIEW_FK_FIELDS;
      const merge = (workingEntity) => {
        const out = { ...workingEntity };
        Object.keys(freshNode).forEach((key) => {
          if (key === "cameras" || key === "views" || key === "updated_at") return;
          const isFk = fkFields.has(key);
          const userTouched = !normalizedEqual(
            workingEntity[key],
            oldServerNode[key],
            isFk,
            RESOLUTION_FIELDS.has(key)
          );
          if (!userTouched) out[key] = freshNode[key];
        });
        out.updated_at = freshNode.updated_at;
        return out;
      };

      // Children handling: do NOT graft freshNode's children into serverTree.
      // Doing so would create a phantom diff (server has new children, working
      // doesn't) that the next changeset would emit as DELETE ops on coworker-
      // added entities. We keep serverTree's existing child set as-is.
      //
      // Coworker-driven add/remove of children on the stale entity is therefore
      // still not detected here. The store-tree endpoint now carries the full
      // hierarchy and would unblock a per-refresh divergence check; tracked as
      // a follow-up rather than wedged into this code path.
      const childKey = kind === "device" ? "cameras" : kind === "camera" ? "views" : null;

      // Build the new server entity: fresh scalars + old children (preserve refs).
      const newServerEntity = { ...freshNode };
      if (childKey) newServerEntity[childKey] = oldServerNode[childKey] || [];

      const updatedServerTree = replaceInTree(state.serverTree, publicId, kind, deepClone(newServerEntity));
      const updatedTree = state.tree.map((d) => {
        if (kind === "device" && d.public_id === publicId) return merge(d);
        return {
          ...d,
          cameras: (d.cameras || []).map((c) => {
            if (kind === "camera" && c.public_id === publicId) return merge(c);
            return {
              ...c,
              views: (c.views || []).map((v) => {
                if (kind === "view" && v.public_id === publicId) return merge(v);
                return v;
              }),
            };
          }),
        };
      });

      return { ...state, serverTree: updatedServerTree, tree: updatedTree };
    }

    default:
      return state;
  }
}

// Apply `fn` to every entity of one kind, wherever it sits in the tree.
// Returns null for an unknown kind so callers can no-op.
function mapEntitiesOfKind(tree, entityType, fn) {
  if (entityType === "device") return tree.map(fn);
  if (entityType === "camera") return tree.map((d) => ({ ...d, cameras: (d.cameras || []).map(fn) }));
  if (entityType === "view") {
    return tree.map((d) => ({
      ...d,
      cameras: (d.cameras || []).map((c) => ({ ...c, views: (c.views || []).map(fn) })),
    }));
  }
  return null;
}

// A bulk action touches many entities but lastError names only one: clear it when
// that entity is in the selection and the action covers the field it flagged.
function clearBulkError(lastError, ids, patch) {
  if (!lastError || !ids.has(lastError.publicId)) return lastError;
  return clearMatchingError(lastError, lastError.publicId, patch);
}

function clearMatchingError(lastError, publicId, patch) {
  if (!lastError) return null;
  if (lastError.publicId !== publicId) return lastError;
  if (!lastError.field || !patch || !(lastError.field in patch)) return lastError;
  return null;
}

function replaceInTree(tree, publicId, kind, fresh) {
  if (kind === "device") {
    return (tree || []).map((d) => (d.public_id === publicId ? fresh : d));
  }
  if (kind === "camera") {
    return (tree || []).map((d) => ({
      ...d,
      cameras: (d.cameras || []).map((c) => (c.public_id === publicId ? fresh : c)),
    }));
  }
  return (tree || []).map((d) => ({
    ...d,
    cameras: (d.cameras || []).map((c) => ({
      ...c,
      views: (c.views || []).map((v) => (v.public_id === publicId ? fresh : v)),
    })),
  }));
}
